import { Controller } from "@hotwired/stimulus"
import Http from "@/lib/Http"
import { pdpCommonProps } from "@/lib/pdpAnalytics"
const MAX_RECENT_VIEWS = 24
export default class extends Controller {
  static targets = [
    "frameTooltip",
    "authenticityTooltip",
    "option",
    "thumbnail",
    "slider",
    "slide",
    "framingLeft",
    "framingRight",
  ]

  static values = {
    listingId: Number,
    cartUrl: String,
    priceUrl: String,
    listingTitle: String,
    listingPrice: Number,
    framingAvailable: Boolean,
    artistName: String,
    framePreviewUrl: String,
    artistName: String,
    reprint: Boolean,
    currentFramingOption: String,
  }

  initialize() {
    // swipe and slide variables
    this.currentIndex = 0
    this.endX = 0
    this.startX = 0

    this.onSpaFrameChange = this.updateFrameFromSpa.bind(this)
    this.onPageFrameChange = this.updateFrameSlider.bind(this)
    this.onTouchStart = this.handleTouchStart.bind(this)
    this.onTouchMove = this.handleTouchMove.bind(this)
    this.onTouchEnd = this.handleTouchEnd.bind(this)

    this.storeRecentViews()
  }

  connect() {
    this.galleryData = this.element.querySelector("#product-gallery")?.dataset || {}
    this.previewImageIndex = this.reprintValue ? 0 : this.slideTargets.findIndex((el) => el.dataset.type === "preview")

    if (this.framingAvailableValue) {
      this.frame = this.optionTargets.find((target) => target.checked)?.value || ""

      const framingOptions = document.getElementById("product-frame-options")
      const overflown = framingOptions.scrollWidth > framingOptions.clientWidth
      if (overflown) {
        this.framingRightTarget.classList.remove("hidden")
      }

      if (this.currentFramingOptionValue) {
        this.displayFrameBack(this.currentFramingOptionValue)
      }
    }

    if (this.hasSliderTarget) {
      this.sliderTarget.addEventListener("touchstart", this.onTouchStart, { passive: true })
      this.sliderTarget.addEventListener("touchmove", this.onTouchMove, { passive: true })
      this.sliderTarget.addEventListener("touchend", this.onTouchEnd, { passive: true })
    }
    this.updateThumbnailStyles(this.currentIndex)
    // listen for frame-change events from Vue.js components
    window.addEventListener("cart:frame-change", this.onSpaFrameChange)
    window.addEventListener("page:frame-change", this.onPageFrameChange)
  }

  disconnect() {
    window.removeEventListener("cart:frame-change", this.onSpaFrameChange)
    window.removeEventListener("page:frame-change", this.onPageFrameChange)
    if (this.hasSliderTarget) {
      this.sliderTarget.removeEventListener("touchstart", this.onTouchStart)
      this.sliderTarget.removeEventListener("touchmove", this.onTouchMove)
      this.sliderTarget.removeEventListener("touchend", this.onTouchEnd)
    }
  }

  handleTouchStart(event) {
    this.startX = event.touches[0].clientX
  }

  handleTouchMove(event) {
    this.endX = event.touches[0].clientX
  }

  handleTouchEnd() {
    const deltaX = this.endX - this.startX

    if (Math.abs(deltaX) > 50) {
      if (deltaX < 0) {
        // minimum swipe distance to be considered a swipe
        this.nextSlide()
      } else {
        this.prevSlide()
      }
    }
  }

  nextSlide() {
    const visibleSlides = this.slideTargets.filter((el) => !el.classList.contains("hidden"))
    if (this.currentIndex < visibleSlides.length - 1) {
      const fromIndex = this.currentIndex
      this.currentIndex++
      this.updateSliderPosition(this.currentIndex)
      this.trackImageChange(fromIndex, this.currentIndex, "swipe")
    }
  }

  prevSlide() {
    if (this.currentIndex > 0) {
      const fromIndex = this.currentIndex
      this.currentIndex--
      this.updateSliderPosition(this.currentIndex)
      this.trackImageChange(fromIndex, this.currentIndex, "swipe")
    }
  }

  updateSliderPosition(index) {
    const sliderWidth = document.querySelector(".slide-wrapper").offsetWidth
    const offset = -index * sliderWidth
    this.sliderTarget.style.transform = `translateX(${offset}px)`

    this.updateThumbnailStyles(index)

    if (this.framingAvailableValue) {
      this.updateFramePreview(this.frame) // clean frame preview when swipe to not "preview" type of images
    }
  }

  updateCart(e) {
    e.preventDefault()
    this.sendGAevents()
    this.capturePdpEvent("pdp_add_to_cart", {
      surface: "inline",
      current_image_index: this.currentIndex,
      value_cents: this.currentValueCents(),
    })

    Http.post(this.cartUrlValue, {
      listing_id: e.params.id,
      framing: this.frame === "unframed" ? "" : this.frame,
    }).then(() => {
      document.dispatchEvent(new Event("product:cart-change")) // communicate with Vue.js CounterBtn and Cart side modal
    })
  }

  updateThumbnailStyles(sliderIndex) {
    const selectedThumbnail = this.thumbnailTargets[sliderIndex]
    const newImageUrl = selectedThumbnail.dataset.previewurl
    this.slideTarget.dataset.src = newImageUrl // set current thumbnail image url for legacy photoswipe gallery

    this.thumbnailTargets.forEach((thumbnail, thumbnailIndex) => {
      if (thumbnailIndex === sliderIndex) {
        thumbnail.classList.add("opacity-100")
        thumbnail.classList.remove("md:opacity-50")
      } else {
        thumbnail.classList.add("md:opacity-50")
        thumbnail.classList.remove("opacity-100")
      }
    })
  }

  changeMainImage(event) {
    const target = event.currentTarget
    const fromIndex = this.currentIndex
    this.currentIndex = parseInt(target.dataset.index, 10)
    this.slideTarget.dataset.index = target.dataset.index // set props for legacy photoswipe gallery
    this.slideTarget.dataset.url = target.dataset.url
    this.updateSliderPosition(this.currentIndex)

    if (this.framingAvailableValue) {
      this.updateFramePreview(this.frame)
    }

    this.trackImageChange(fromIndex, this.currentIndex, "thumbnail")
  }

  selectFrame(e) {
    this.frame = e.target.value
    this.fetchPrice()

    if (this.currentIndex !== this.previewImageIndex) {
      this.currentIndex = this.previewImageIndex

      this.updateSliderPosition(this.currentIndex)
    }

    this.updateFramePreview(e.target.value)
    this.displayFrameBack(e.target.value)
    this.fetchFramePreview()
  }

  displayFrameBack(option) {
    this.thumbnailTargets
      .filter((el) => {
        return el.dataset.type === "frame"
      })
      .forEach((el) => {
        if (el.dataset.option === option) {
          el.classList.remove("hidden")
        } else {
          el.classList.add("hidden")
        }
      })
  }

  updateFrameFromSpa(e) {
    this.frame = e.detail
    this.updateFramePreview(e.detail)
  }

  fetchPrice() {
    return Http.get(
      this.priceUrlValue,
      { params: { option: this.frame === "unframed" ? null : this.frame } },
      { headers: { Accept: "text/vnd.turbo-stream.html" } },
    )
  }

  fetchFramePreview() {
    return Http.get(this.framePreviewUrlValue, {
      params: {
        listing_id: this.listingIdValue,
        preview_option: this.frame,
      },
    })
  }

  updateFramePreview(framingOption) {
    const clearFrameClasses = (element) => {
      const frameClasses = element.classList.toString().match(/\bis-\S+/g) || []
      element.classList.remove(...frameClasses)
    }

    const previewImage = this.slideTargets[this.previewImageIndex]
    const imageWrapper = document.querySelector(".main-image-wrapper")
    const framePreview = previewImage.closest(".frame-wrapper")
    const sliderWrapper = document.querySelector(".product-image-canvas")
    const frameLabel = framingOption.split(".")[0]
    const canvasStyleFraming = imageWrapper.dataset.canvasStyleFraming === "true"
    const paperStyleFraming = imageWrapper.dataset.paperStyleFraming === "true"

    clearFrameClasses(framePreview)
    imageWrapper.classList.remove("py-10")
    previewImage.classList.remove("p-16")
    sliderWrapper.classList.remove("py-2")

    if (frameLabel && !["unframed", "stretching"].includes(frameLabel)) {
      framePreview.classList.add("is-framed", `is-${frameLabel}`)

      if (paperStyleFraming) {
        previewImage.classList.add("p-16")
        framePreview.classList.add("is-paper")
      }

      if (canvasStyleFraming) framePreview.classList.add("is-canvas")
      if (this.reprintValue) framePreview.classList.add("is-reprint")

      imageWrapper.classList.add("py-10")
      sliderWrapper.classList.add("py-2")
    }
  }

  scrollToOption(e) {
    const scrollingDirection = e.currentTarget.dataset.direction
    const isRight = scrollingDirection === "right"
    const parent = document.getElementById("product-frame-options")
    parent.scrollLeft = isRight ? parent.clientWidth : -parent.clientWidth
    this.framingRightTarget.classList.toggle("hidden", isRight)
    this.framingLeftTarget.classList.toggle("hidden", !isRight)
  }

  toggleFrameTooltip() {
    this.frameTooltipTarget.classList.toggle("hidden")
  }

  toggleAuthenticityTooltip() {
    this.authenticityTooltipTarget.classList.toggle("hidden")
  }

  storeRecentViews() {
    try {
      let recentViews = []

      if (localStorage.recentViewsId) {
        recentViews = JSON.parse(localStorage.recentViewsId)
          .filter((listingId) => listingId !== this.listingIdValue)
          .slice(0, MAX_RECENT_VIEWS)
      }

      recentViews.push(this.listingIdValue)

      localStorage.recentViewsId = JSON.stringify(recentViews)
    } catch (error) {
      console.error("Error storing recent views:", error)
    }
  }

  sendGAevents() {
    pushEcommerceEvent({
      event: "addToCart",
      ecommerce: {
        add: {
          products: [
            {
              name: this.listingTitleValue,
              id: this.listingIdValue,
              price: parseInt(this.listingPriceValue),
              brand: this.artistNameValue,
              quantity: 1,
            },
          ],
        },
      },
    })
    // GA4
    const item = this.analyticsDataAttributes()
    pushEcommerceEvent({
      event: "add_to_cart",
      ecommerce: {
        currency: "AUD",
        value: item.price,
        items: [item],
      },
    })
  }

  updateFrameSlider() {
    const checkedInput = document.querySelector('input[name="frame"]:checked')
    const parent = document.getElementById("product-frame-options")
    const isOverflown = parent.scrollWidth > parent.clientWidth

    if (isOverflown) {
      const toggleButtonVisibility = (button, show) => {
        button.classList.toggle("hidden", !show)
      }

      if (["black", "white"].includes(checkedInput.value)) {
        parent.scrollLeft = parent.clientWidth
        toggleButtonVisibility(this.framingLeftTarget, true)
      } else {
        parent.scrollLeft = -parent.clientWidth
        toggleButtonVisibility(this.framingRightTarget, true)
      }
    }
  }

  capturePdpEvent(event, props) {
    window.posthog?.capture(event, { ...pdpCommonProps(this.galleryData), ...props })
  }

  // Live cart value in cents — kept current by the framing price Turbo Stream.
  analyticsDataAttributes() {
    return JSON.parse(document.getElementById(`pdp-analytics-attributes-${this.listingIdValue}`).textContent)
  }

  currentValueCents() {
    const cents = parseInt(document.getElementById(`pdp-cart-value-cents-${this.listingIdValue}`)?.textContent, 10)
    return Number.isNaN(cents) ? null : cents
  }

  trackImageChange(fromIndex, toIndex, source) {
    if (fromIndex === toIndex) return
    this.capturePdpEvent("pdp_gallery_image_changed", { from_index: fromIndex, to_index: toIndex, source })
  }
}
